import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Header from '../components/Header';
import { userSession, pointsApi, voucherApi, productApi, mediaApi } from '../services';
import { formatMoney } from '../utils/utils';

function OrderSummaryPage({ onNavigate }) {
  const [userInfo, setUserInfo] = useState(null);
  const [cart, setCart] = useState([]);
  const [userPoints, setUserPoints] = useState(0);
  const [pointRedeemValue, setPointRedeemValue] = useState(1000); // Default value, will be updated from API
  const [couponCode, setCouponCode] = useState('');
  const [pointsToUse, setPointsToUse] = useState(0);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [pointsDiscount, setPointsDiscount] = useState(0);
  const [voucherData, setVoucherData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [errors, setErrors] = useState({});
  const [redeemedHistory, setRedeemedHistory] = useState([]);
  const [loadingRedeemedHistory, setLoadingRedeemedHistory] = useState(false);
  const [hasLoadedRedeemedHistory, setHasLoadedRedeemedHistory] = useState(false);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const token = localStorage.getItem('token');
  
  // Ref for managing timeouts
  const errorTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Cleanup function for timeouts
  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  // Debounced error clearing function
  const clearErrorsWithDelay = useCallback((delay = 3000) => {
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }
    errorTimeoutRef.current = setTimeout(() => {
      setErrors({});
    }, delay);
  }, []);

  useEffect(() => {
    // Get session data
    const session = userSession.getSession();
    console.log('Session data:', session);
    if (session && session.cart && session.cart.length > 0) {
      setUserInfo(session);
      setCart(session.cart);
      if (session.image) {
        setImagePreview(session.image);
      }
      if(token && !hasLoadedRedeemedHistory){
        loadUserPoints();
        loadRedeemedHistory();
        setHasLoadedRedeemedHistory(true);
      }
      setSessionLoaded(true);
    } else {
      // Redirect back to order page if no cart
      console.log('No session or cart, redirecting to order page');
      setTimeout(() => {
        onNavigate('/order');
      }, 1000);
    }
  }, [onNavigate, token, hasLoadedRedeemedHistory]);

  // Cập nhật session khi cart thay đổi (chỉ khi có sản phẩm đã đổi và không phải do loadRedeemedHistory)
  useEffect(() => {
    const hasRedeemedItems = cart.some(item => item.is_redeemed);
    if (cart.length > 0 && hasRedeemedItems && hasLoadedRedeemedHistory) {
      userSession.updateSession({ cart: cart });
    }
  }, [cart]);

  // State để quản lý số lượng đổi cho từng sản phẩm - đã được tối ưu
  const [redeemQuantities, setRedeemQuantities] = useState({});

  // Thêm state để quản lý loading cho từng sản phẩm
  const [redeemLoading, setRedeemLoading] = useState({});

  // Hàm helper để tính toán thông tin đổi điểm của sản phẩm
  const getProductRedeemInfo = (productId) => {
    const productItem = cart.find(item => item.product_id === productId);
    if (!productItem) {
      return {
        totalQuantity: 0,
        redeemedCount: 0,
        maxCanRedeem: 0,
        remainingRedeem: 0,
        canRedeem: false
      };
    }
    
    const totalQuantity = productItem.quantity;
    const redeemedCount = productItem.redeemed_count || 0;
    const maxCanRedeem = productItem.redeemed_quantity || 0;
    const remainingRedeem = Math.min(maxCanRedeem - redeemedCount, totalQuantity - redeemedCount);
    
    return {
      totalQuantity,
      redeemedCount,
      maxCanRedeem,
      remainingRedeem,
      canRedeem: remainingRedeem > 0 && maxCanRedeem > 0
    };
  };

  // Optimized memoized calculation functions
  const getSubtotal = useMemo(() => {
    return cart.reduce((total, item) => {
      // Tính tiền cho phần chưa đổi điểm
      const redeemedCount = item.redeemed_count || 0;
      const payableQuantity = item.quantity - redeemedCount;
      return total + (item.price * payableQuantity);
    }, 0);
  }, [cart]);

  const getTotalItems = useMemo(() => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  }, [cart]);

  const getFinalTotal = useMemo(() => {
    return Math.max(0, getSubtotal - couponDiscount - pointsDiscount);
  }, [getSubtotal, couponDiscount, pointsDiscount]);

  // Check if cart has products with category_type = 3
  const hasCategoryType3 = useMemo(() => {
    return cart.some(item => item.category_type === 3);
  }, [cart]);

  // Optimized event handlers with useCallback
  const updateRedeemQuantity = useCallback((productId, newQuantity) => {
    const { redeemedCount, maxCanRedeem, totalQuantity } = getProductRedeemInfo(productId);
    
    // Giới hạn số lượng trong phạm vi hợp lệ
    const validQuantity = Math.max(0, Math.min(newQuantity, maxCanRedeem, totalQuantity));
    
    setRedeemQuantities(prev => ({
      ...prev,
      [productId]: validQuantity
    }));
  }, [getProductRedeemInfo]);

  const toggleRedeemProduct = useCallback(async (productId, quantity = 1) => {
    const { remainingRedeem, maxCanRedeem } = getProductRedeemInfo(productId);
    const actualQuantity = Math.min(quantity, remainingRedeem);
    
    if (actualQuantity <= 0) {
      const message = maxCanRedeem <= 0 
        ? 'Sản phẩm này không thể đổi bằng điểm'
        : 'Bạn đã đổi tối đa số lượng cho phép';
      setErrors({ redeem: message });
      clearErrorsWithDelay();
      return;
    }

    setRedeemLoading(prev => ({ ...prev, [productId]: true }));
    
    try {
      const updatedCart = cart.map(item => {
        if (item.product_id === productId) {
          // Tính số lượng đã đổi hiện tại
          const currentRedeemedCount = item.redeemed_count || 0;
          // Tính số lượng sẽ đổi mới (không vượt quá actualQuantity và quantity của item)
          const newRedeemedCount = Math.min(
            currentRedeemedCount + actualQuantity, 
            item.quantity,
            item.redeemed_quantity || 0 // Giới hạn theo số lượng có thể đổi
          );
          
          return { 
            ...item, 
            redeemed_count: newRedeemedCount,
            is_redeemed: newRedeemedCount > 0 
          };
        }
        return item;
      });
      
      // Debug log để kiểm tra
      console.log('Debug toggleRedeemProduct:', {
        productId,
        actualQuantity,
        originalItem: cart.find(item => item.product_id === productId),
        updatedItem: updatedCart.find(item => item.product_id === productId)
      });
      
      setCart(updatedCart);
      setRedeemQuantities(prev => ({ ...prev, [productId]: 0 }));
      setErrors({}); 
    } catch (error) {
      console.error('Error redeeming product:', error);
      setErrors({ redeem: 'Có lỗi xảy ra khi đổi điểm' });
    } finally {
      setRedeemLoading(prev => ({ ...prev, [productId]: false }));
    }
  }, [cart, getProductRedeemInfo, clearErrorsWithDelay]);

  const cancelRedeemProduct = useCallback((productId) => {
    const updatedCart = cart.map(item => 
      item.product_id === productId ? { 
        ...item, 
        is_redeemed: false,
        redeemed_count: 0 
      } : item
    );
    setCart(updatedCart);
    setRedeemQuantities(prev => ({ ...prev, [productId]: 0 }));
  }, [cart]);

  // Optimized RedeemStepper component with memoization
  const RedeemStepper = React.memo(({ productId }) => {
    const { remainingRedeem } = getProductRedeemInfo(productId);
    const currentValue = redeemQuantities[productId] || 0;
    const isLoading = redeemLoading[productId];
    
    if (remainingRedeem <= 1) return null;

    const canDecrease = currentValue > 0 && !isLoading;
    const canIncrease = currentValue < remainingRedeem && !isLoading;
    const canApply = currentValue > 0 && !isLoading;

    return (
      <div style={stepperContainerStyle}>
        <span style={stepperLabelStyle}>Số lượng đổi:</span>
        <div style={stepperControlsStyle}>
          <button
            onClick={() => updateRedeemQuantity(productId, currentValue - 1)}
            disabled={!canDecrease}
            style={{
              ...stepperButtonStyle,
              cursor: canDecrease ? 'pointer' : 'not-allowed',
              opacity: canDecrease ? 1 : 0.5
            }}
          >
            -
          </button>
          <span style={stepperValueStyle}>
            {currentValue}
          </span>
          <button
            onClick={() => updateRedeemQuantity(productId, currentValue + 1)}
            disabled={!canIncrease}
            style={{
              ...stepperButtonStyle,
              cursor: canIncrease ? 'pointer' : 'not-allowed',
              opacity: canIncrease ? 1 : 0.5
            }}
          >
            +
          </button>
        </div>
        <button
          onClick={() => toggleRedeemProduct(productId, currentValue)}
          disabled={!canApply}
          style={{
            ...applyButtonStyle,
            cursor: canApply ? 'pointer' : 'not-allowed',
            opacity: canApply ? 1 : 0.5
          }}
        >
          {isLoading ? '...' : 'Áp dụng'}
        </button>
      </div>
    );
  });

  const loadUserPoints = async () => {
    try {
      const response = await pointsApi.getCustomerInfo();
      console.log('Customer info response:', response);
      if (response.ok && response.payload) {
        setUserPoints(response.payload.customer_point || 0);
        setPointRedeemValue(response.payload.point_redeem_value || 1000);
      } else {
        setUserPoints(0);
        setPointRedeemValue(1000);
      }
    } catch (error) {
      console.error('Error loading customer info:', error);
      setUserPoints(0);
      setPointRedeemValue(1000);
    }
  };

  const loadRedeemedHistory = async () => {
    setLoadingRedeemedHistory(true);
    const session = userSession.getSession();
    try {
      const response = await pointsApi.getCustomerRedeemProduct();
      if (response.ok && Array.isArray(response.payload)) {
        const redeemedItems = response.payload;
        console.log('redeemedItems', redeemedItems);
        
        
        // Filter only items that are in the current cart
        const cartProductIds = session.cart.map(item => item.product_id);
        console.log('cartProductIds', session.cart);
        
        const availableRedeemedItems = redeemedItems.filter(item => cartProductIds.includes(item.product_id));
        console.log('availableRedeemedItems', availableRedeemedItems);
        setRedeemedHistory(availableRedeemedItems);
        
        // Không tự động đánh dấu sản phẩm đã đổi, chỉ lưu thông tin để hiển thị nút
        const updatedCart = session.cart.map(item => {
          // Tìm số lượng sản phẩm đã đổi trong lịch sử
          const redeemedItems = availableRedeemedItems.filter(redeemed => redeemed.product_id === item.product_id);
          const redeemedQuantity = redeemedItems.reduce((total, redeemed) => total + (redeemed.quantity || 1), 0);
          
          // Kiểm tra xem có thể đổi được không (số lượng còn lại > 0)
          const canRedeem = redeemedQuantity > 0;
          
          return {
            ...item,
            can_redeem: canRedeem,
            redeemed_quantity: redeemedQuantity, // Số lượng có thể đổi
            is_redeemed: false // Không tự động đổi
          };
        });
        setCart(updatedCart);
      } else {
        setRedeemedHistory([]);
      }
    } catch (error) {
      console.error('Error loading redeemed history:', error);
      setRedeemedHistory([]);
    } finally {
      setLoadingRedeemedHistory(false);
    }
  };

  // Optimized item display price calculation with memoization
  const getItemDisplayPrice = useCallback((item) => {
    const redeemedCount = item.redeemed_count || 0;
    const payableQuantity = item.quantity - redeemedCount;
    const totalPrice = item.price * payableQuantity;
    
    if (redeemedCount > 0) {
      return `${formatMoney(totalPrice)}đ (${redeemedCount} miễn phí)`;
    } else {
      return `${formatMoney(item.price * item.quantity)}đ`;
    }
  }, []);

  // Compute voucher discount against a given base amount (after points)
  const calculateVoucherDiscount = useCallback((voucher, baseAmount) => {
    if (!voucher || !baseAmount || baseAmount <= 0) {
      return { discount: 0, error: '' };
    }

    const currentDate = new Date();
    const applyFrom = new Date(voucher.apply_from);
    const applyTo = new Date(voucher.apply_to);

    if (voucher.status !== 0) {
      return { discount: 0, error: 'Mã giảm giá không còn hiệu lực' };
    }

    if (currentDate < applyFrom || currentDate > applyTo) {
      return { discount: 0, error: 'Mã giảm giá đã hết hạn hoặc chưa có hiệu lực' };
    }

    const minAmount = parseFloat(voucher.min_amount);
    if (baseAmount < minAmount) {
      return { discount: 0, error: `Đơn hàng tối thiểu ${formatMoney(minAmount)}đ để áp dụng mã này` };
    }

    let discount = 0;
    const price = parseFloat(voucher.price);
    const maxAmount = parseFloat(voucher.max_amount);

    if (voucher.category === 2) {
      discount = (baseAmount * price) / 100;
      if (maxAmount > 0) {
        discount = Math.min(discount, maxAmount);
      }
    } else {
      discount = Math.min(price, baseAmount);
    }

    return { discount, error: '' };
  }, []);

  // Handle coupon code
  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      setErrors({ coupon: 'Vui lòng nhập mã giảm giá' });
      return;
    }

    setLoading(true);
    try {
      const response = await voucherApi.getVoucherByToken(couponCode);

      if (response.payload) {
        const voucher = response.payload;
        const baseAmount = Math.max(0, getSubtotal - pointsDiscount);
        const { discount, error } = calculateVoucherDiscount(voucher, baseAmount);

        if (error) {
          setErrors(prev => ({ ...prev, coupon: error }));
          setCouponDiscount(0);
          setVoucherData(null);
          return;
        }

        setCouponDiscount(discount);
        setVoucherData(voucher);
        setErrors(prev => ({ ...prev, coupon: '' }));
      }
    } catch (error) {
      console.error('Error applying coupon:', error);
      setErrors({ coupon: 'Mã giảm giá không hợp lệ' });
      setCouponDiscount(0);
      setVoucherData(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePointsChange = (value) => {
    const points = Math.max(0, Math.min(value, userPoints));
    setPointsToUse(points);
    
    // Auto apply when typing
    if (points >= 0 && points <= userPoints) {
      const discount = points * pointRedeemValue;
      const maxDiscount = Math.max(0, getSubtotal);
      const appliedDiscount = Math.min(discount, maxDiscount);

      setPointsDiscount(appliedDiscount);
      setErrors(prev => ({ ...prev, points: '' }));
    }
  };

  // Recalculate voucher discount whenever points change
  useEffect(() => {
    if (voucherData) {
      const baseAmount = Math.max(0, getSubtotal - pointsDiscount);
      const { discount, error } = calculateVoucherDiscount(voucherData, baseAmount);
      if (error) {
        setCouponDiscount(0);
        setErrors(prev => ({ ...prev, coupon: error }));
      } else {
        setCouponDiscount(discount);
        setErrors(prev => ({ ...prev, coupon: '' }));
      }
    }
  }, [pointsDiscount, getSubtotal, voucherData, calculateVoucherDiscount]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setErrors((prev) => ({
          ...prev,
          image: "Kích thước ảnh không được vượt quá 5MB",
        }));
        return;
      }
      
      setImageFile(file);
      setErrors((prev) => ({ ...prev, image: "" }));
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const uploadImageToServer = async (file) => {
    try {
      setUploadingImage(true);
      const response = await mediaApi.uploadImage(file);
      if (response.ok && response.payload) {
        return response.payload; // Return the image URL
      }
      throw new Error('Upload failed');
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCheckout = async () => {
    // Validate: không cho phép có cả combo giờ (category_type = 2) và vé tháng (category_type = 3) trong cùng đơn hàng
    const hasComboHour = cart.some((item) => item.category_type === 2);
    const hasSubscription = cart.some((item) => item.category_type === 3 && !item.is_free_subscription);
    
    if (hasComboHour && hasSubscription) {
      setErrors({ general: 'Đơn hàng không thể vừa có combo giờ, vừa có vé tháng' });
      setCheckingOut(false);
      return;
    }

    // Validate: không cho phép có nhiều hơn 1 loại vé tháng (category_type = 3) trong đơn hàng
    const subscriptionProducts = cart.filter(
      (item) => item.category_type === 3 && !item.is_free_subscription
    );
    
    if (subscriptionProducts.length > 1) {
      setErrors({ general: 'Trong 1 đơn hàng chỉ được chọn 1 loại vé tháng' });
      setCheckingOut(false);
      return;
    }

    setCheckingOut(true);
    
    try {
      // Upload image if exists
      let imageUrl = null;
      if (imageFile) {
        try {
          imageUrl = await uploadImageToServer(imageFile);
        } catch (error) {
          console.error("Error uploading image:", error);
          setErrors({ general: "Không thể upload ảnh. Vui lòng thử lại." });
          setCheckingOut(false);
          return;
        }
      } else if (imagePreview && imagePreview.startsWith('http')) {
        // If preview is already a URL (from session), use it
        imageUrl = imagePreview;
      }

      // Prepare order data for API
      const listProducts = cart
        .map(item => {
          const redeemedCount = item.redeemed_count || 0;
          const payableQuantity = Math.max(0, (item.quantity || 0) - redeemedCount);
          return payableQuantity > 0
            ? {
                product_id: item.product_id,
                name: item.name,
                quantity: payableQuantity
              }
            : null;
        })
        .filter(Boolean);

      const listProductRedeem = cart
        .filter(item => (item.redeemed_count || 0) > 0)
        .map(item => ({
          product_id: item.product_id,
          name: item.name,
          quantity: item.redeemed_count || 0
        }));

      const orderData = {
        mobile_number: userInfo.phone,
        name: userInfo.name,
        list_products: listProducts,
        list_product_redeem: listProductRedeem,
        redeem_point_used: pointsToUse,
        voucher_token_id: voucherData ? voucherData.voucher_token_id : null,
        ...(imageUrl && { customer_img_url: imageUrl })
      };

      console.log('Submitting order:', orderData);
      
      // Call order-checkin API
      const response = await voucherApi.submitOrderCheckin(orderData);
      
      if (response.success || response.payload) {
        // Save final order data to session for success page
        const finalOrderData = {
          items: cart,
          subtotal: getSubtotal,
          couponCode: couponCode,
          couponDiscount: couponDiscount,
          pointsUsed: pointsToUse,
          pointsDiscount: pointsDiscount,
          finalTotal: getFinalTotal,
          totalItems: getTotalItems,
          orderTime: new Date().toISOString(),
          voucherData: voucherData,
          redeemedProducts: cart.filter(item => (item.redeemed_count || 0) > 0),
          orderResponse: response.payload
        };

        userSession.updateSession({ 
          finalOrder: finalOrderData,
          ...(imageUrl && { image: imageUrl })
        });
        onNavigate('/checkin-success');
      } else {
        setErrors({ general: response.message || 'Có lỗi xảy ra khi đặt hàng. Vui lòng thử lại.' });
      }
    } catch (error) {
      console.error('Error during checkout:', error);
      setErrors({ general: error.response.data.message || 'Có lỗi xảy ra khi đặt hàng. Vui lòng thử lại.' });
    } finally {
      setCheckingOut(false);
    }
  };

  if (!sessionLoaded || !userInfo || cart.length === 0) {
    console.log('Loading condition:', { 
      sessionLoaded,
      userInfo: !!userInfo, 
      cartLength: cart.length,
      userInfoKeys: userInfo ? Object.keys(userInfo) : null,
      cart: cart
    });
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: 10
      }}>
        <div>Loading...</div>
        <div style={{ fontSize: 12, color: '#666' }}>
          {!sessionLoaded ? 'Đang tải session...' : !userInfo ? 'Chưa có userInfo' : 'Đang tải giỏ hàng...'}
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ background: '#fff', width: 340, minHeight: 600, borderRadius: 8, padding: 20, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        <Header onCartClick={() => { onNavigate('/order'); }} onMenuClick={() => { onNavigate('/') }} />
        
        <h2 style={{ textAlign: 'center', marginTop: 10, marginBottom: 24, fontSize: 24, fontWeight: 600, color: '#5b6754' }}>
          Xác nhận đơn hàng
        </h2>

        {/* Order Items */}
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Sản phẩm đã chọn</h3>
          <div style={{ background: '#f8f8f8', borderRadius: 12, padding: 16, maxHeight: 400, overflowY: 'auto' }}>
            {cart.map(item => (
              <div key={item.product_id} style={{ 
                display: 'flex', 
                flexDirection: 'column',
                marginBottom: 12,
                paddingBottom: 12,
                borderBottom: cart.indexOf(item) < cart.length - 1 ? '1px solid #eee' : 'none'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: 8
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>
                      {item.name}
                      {(item.redeemed_count || 0) > 0 && (
                        <span style={{ 
                          background: '#F9A23B', 
                          color: '#fff', 
                          fontSize: 10, 
                          padding: '2px 6px', 
                          borderRadius: 8, 
                          marginLeft: 8 
                        }}>
                          Đã đổi {item.redeemed_count}
                        </span>
                      )}
                    </div>
                    <div style={{ color: '#888', fontSize: 12 }}>
                      {formatMoney(item.price)}đ x {item.quantity}
                    </div>
                  </div>
                  <div style={{ fontWeight: 600, color: (item.redeemed_count || 0) > 0 ? '#F9A23B' : '#5b6754' }}>
                    {getItemDisplayPrice(item)}
                  </div>
                </div>
                
                {/* Nút đổi bằng điểm - đã được tối ưu */}
                {token && item.can_redeem && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: 12, color: '#666' }}>
                        {(() => {
                          const { remainingRedeem, maxCanRedeem } = getProductRedeemInfo(item.product_id);
                          return `Có thể đổi: ${remainingRedeem}/${maxCanRedeem} sản phẩm`;
                        })()}
                      </div>
                    </div>
                    
                    {/* Sử dụng Stepper component mới */}
                    <RedeemStepper productId={item.product_id} />
                    
                    {/* Thông báo lỗi đổi điểm */}
                    {errors.redeem && (
                      <div style={{ 
                        background: '#fee', 
                        border: '1px solid #fcc', 
                        borderRadius: 8, 
                        padding: 8, 
                        marginBottom: 8,
                        color: '#c33',
                        fontSize: 12,
                        textAlign: 'center'
                      }}>
                        {errors.redeem}
                      </div>
                    )}

                    <RedeemActionButtons 
                      productId={item.product_id}
                      onCancel={cancelRedeemProduct}
                      onRedeem={toggleRedeemProduct}
                      isLoading={redeemLoading[item.product_id]}
                      getProductRedeemInfo={getProductRedeemInfo}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Points Section */}
        {token && <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>
            Sử dụng điểm thưởng
          </h3>
          <div style={{ fontSize: 14, color: '#666', marginBottom: 8 }}>
            Điểm khả dụng: <strong>{formatMoney(userPoints)}</strong> điểm (1 điểm = {formatMoney(pointRedeemValue)}đ)
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input
              type="number"
              placeholder="Nhập số điểm"
              value={pointsToUse}
              onChange={(e) => handlePointsChange(parseInt(e.target.value) || 0)}
              min="0"
              max={userPoints}
              style={{
                flex: 1,
                height: 40,
                border: '1px solid #ddd',
                borderRadius: 20,
                padding: '0 16px',
                fontSize: 14,
                outline: 'none'
              }}
            />
            <button
              onClick={() => handlePointsChange(Math.min(userPoints, Math.floor(getSubtotal / pointRedeemValue)))}
              style={{
                background: 'transparent',
                color: '#5b6754',
                border: '1px solid #5b6754',
                borderRadius: 20,
                padding: '0 16px',
                fontSize: 14,
                cursor: 'pointer'
              }}
            >
              Tối đa
            </button>
          </div>
          {errors.points && (
            <div style={{ color: '#ff4444', fontSize: 12 }}>{errors.points}</div>
          )}
          {pointsDiscount > 0 && (
            <div style={{ color: '#4CAF50', fontSize: 12 }}>
              ✓ Sử dụng {formatMoney(pointsToUse)} điểm: -{formatMoney(pointsDiscount)}đ
            </div>
          )}
        </div>}

        {/* Image Upload Section - Only show if logged in and has category_type = 3 products */}
        {token && hasCategoryType3 && (
          <div style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Ảnh khách hàng (tùy chọn)</h3>
            
            {imagePreview ? (
              <div style={{ position: 'relative', marginBottom: 12 }}>
                <img 
                  src={imagePreview} 
                  alt="Preview" 
                  style={{
                    width: '100%',
                    maxHeight: 200,
                    objectFit: 'cover',
                    borderRadius: 12,
                    border: '1px solid #5b6754'
                  }}
                />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'rgba(255, 68, 68, 0.9)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '50%',
                    width: 32,
                    height: 32,
                    cursor: 'pointer',
                    fontSize: 18,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold'
                  }}
                >
                  ×
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  style={{
                    flex: 1,
                    height: 44,
                    background: '#5b6754',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 22,
                    fontSize: 14,
                    cursor: 'pointer',
                    fontFamily: 'NeueEinstellung',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8
                  }}
                >
                  📷 Chụp ảnh
                </button>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    flex: 1,
                    height: 44,
                    background: '#fff',
                    color: '#5b6754',
                    border: '1.5px solid #5b6754',
                    borderRadius: 22,
                    fontSize: 14,
                    cursor: 'pointer',
                    fontFamily: 'NeueEinstellung',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8
                  }}
                >
                  📁 Chọn ảnh
                </button>
              </div>
            )}
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              style={{ display: 'none' }}
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleImageSelect}
              style={{ display: 'none' }}
            />
            
            {errors.image && (
              <div style={{ color: "#ff4444", fontSize: 12, marginTop: 4 }}>
                {errors.image}
              </div>
            )}
          </div>
        )}

        {/* Coupon Section */}
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Mã giảm giá</h3>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input
              type="text"
              placeholder="Nhập mã giảm giá"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              style={{
                flex: 1,
                height: 40,
                border: '1px solid #ddd',
                borderRadius: 20,
                padding: '0 16px',
                fontSize: 14,
                outline: 'none'
              }}
            />
            <button
              onClick={applyCoupon}
              disabled={loading}
              style={{
                background: '#5b6754',
                color: '#fff',
                border: 'none',
                borderRadius: 20,
                padding: '0 20px',
                fontSize: 14,
                cursor: 'pointer',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Đang xử lý...' : 'Áp dụng'}
            </button>
          </div>
          {errors.coupon && (
            <div style={{ color: '#ff4444', fontSize: 12 }}>{errors.coupon}</div>
          )}
          {couponDiscount > 0 && voucherData && (
            <div style={{ color: '#4CAF50', fontSize: 12 }}>
              ✓ Đã áp dụng "{voucherData.name}": -{formatMoney(couponDiscount)}đ
            </div>
          )}
        </div>

        {/* Order Summary */}
        <div style={{ background: '#f8f8f8', borderRadius: 12, padding: 16, marginBottom: 24 }}>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Tổng cộng</h3>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span>Tạm tính:</span>
            <span>{formatMoney(getSubtotal)}đ</span>
          </div>
          {pointsDiscount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, color: '#4CAF50' }}>
              <span>Điểm thưởng:</span>
              <span>-{formatMoney(pointsDiscount)}đ</span>
            </div>
          )}
          {couponDiscount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, color: '#4CAF50' }}>
              <span>Giảm giá:</span>
              <span>-{formatMoney(couponDiscount)}đ</span>
            </div>
          )}
          

          <hr style={{ border: 'none', borderTop: '1px solid #ddd', margin: '12px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 18, fontWeight: 600, color: '#5b6754' }}>
            <span>Thành tiền:</span>
            <span>{formatMoney(getFinalTotal)}đ</span>
          </div>
        </div>

        {/* Show general error */}
        {errors.general && (
          <div style={{ 
            background: '#fee', 
            border: '1px solid #fcc', 
            borderRadius: 8, 
            padding: 12, 
            marginBottom: 16,
            color: '#c33',
            fontSize: 14,
            textAlign: 'center'
          }}>
            {errors.general}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: 12, marginTop: 'auto' }}>
          <button
            onClick={() => onNavigate('/order')}
            disabled={checkingOut}
            style={{
              flex: 1,
              height: 44,
              background: 'transparent',
              color: '#5b6754',
              border: '2px solid #5b6754',
              borderRadius: 24,
              fontSize: 16,
              cursor: checkingOut ? 'not-allowed' : 'pointer',
              fontFamily: 'NeueEinstellung',
              opacity: checkingOut ? 0.5 : 1
            }}
          >
            Quay lại
          </button>
          <button
            onClick={handleCheckout}
            disabled={checkingOut || uploadingImage}
            style={{
              flex: 2,
              height: 44,
              background: '#5b6754',
              color: '#fff',
              border: 'none',
              borderRadius: 24,
              fontSize: 16,
              fontWeight: 500,
              cursor: (checkingOut || uploadingImage) ? 'not-allowed' : 'pointer',
              fontFamily: 'NeueEinstellung',
              opacity: (checkingOut || uploadingImage) ? 0.7 : 1
            }}
          >
            {uploadingImage ? 'Đang upload ảnh...' : checkingOut ? 'Đang xử lý...' : 'Xác nhận đặt hàng'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Optimized component for redeem action buttons
const RedeemActionButtons = React.memo(({ productId, onCancel, onRedeem, isLoading, getProductRedeemInfo }) => {
  const { remainingRedeem, redeemedCount } = getProductRedeemInfo(productId);
  
  // Multiple items case with cancel button
  if (redeemedCount > 0 && remainingRedeem !== 1) {
    return (
      <div style={actionButtonContainerStyle}>
        <button
          onClick={() => onCancel(productId)}
          disabled={isLoading}
          style={{
            ...cancelButtonStyle,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.5 : 1
          }}
        >
          {isLoading ? '...' : `Hủy đổi ${redeemedCount > 1 ? `(${redeemedCount})` : ''}`}
        </button>
      </div>
    );
  }
  
  // Single item case
  if (remainingRedeem === 1) {
    return (
      <div style={actionButtonContainerStyle}>
        {redeemedCount > 0 ? (
          <button
            onClick={() => onCancel(productId)}
            style={cancelButtonStyle}
          >
            Hủy đổi
          </button>
        ) : (
          <button
            onClick={() => onRedeem(productId, 1)}
            style={redeemButtonStyle}
          >
            Đổi voucher
          </button>
        )}
      </div>
    );
  }
  
  return null;
});

// Styles for better maintainability
const stepperContainerStyle = {
  display: 'flex', 
  alignItems: 'center', 
  gap: 8, 
  marginTop: 8
};

const stepperLabelStyle = {
  fontSize: 12, 
  color: '#666'
};

const stepperControlsStyle = {
  display: 'flex', 
  alignItems: 'center', 
  border: '1px solid #ddd', 
  borderRadius: 16
};

const stepperButtonStyle = {
  width: 28,
  height: 28,
  background: 'none',
  border: 'none',
  fontSize: 16
};

const stepperValueStyle = {
  minWidth: 30, 
  textAlign: 'center', 
  fontSize: 12
};

const applyButtonStyle = {
  background: '#F9A23B',
  color: '#fff',
  border: 'none',
  borderRadius: 16,
  padding: '4px 12px',
  fontSize: 12
};

const actionButtonContainerStyle = {
  display: 'flex', 
  justifyContent: 'flex-end'
};

const cancelButtonStyle = {
  background: '#ff4444',
  color: '#fff',
  border: 'none',
  borderRadius: 16,
  padding: '6px 12px',
  fontSize: 12,
  fontWeight: 500
};

const redeemButtonStyle = {
  background: '#F9A23B',
  color: '#fff',
  border: 'none',
  borderRadius: 16,
  padding: '6px 12px',
  fontSize: 12,
  fontWeight: 500
};

export default OrderSummaryPage; 